import React, { useContext, useEffect, useState } from 'react';
import { createAuth0Client } from '@auth0/auth0-spa-js';
import { setUserReducer } from '@client/redux/user/userActions';
import { setCookie } from '@client/shared/utils/utils/utils';
import { resetUserReducer } from '@client/redux/user';
import { useNavigate } from 'react-router-dom';

// Constants
import { AUTH_0_LOGIN } from '@client/definitions/constants';

export const Auth0Context = React.createContext();

export const useAuth0 = () => useContext(Auth0Context);

export const Auth0Provider = ({ children, dispatch, ...initOptions }) => {
  const [isAuthenticated, setIsAuthenticated] = useState();
  const [user, setUser] = useState();
  const [auth0Client, setAuth0] = useState();
  const [loading, setLoading] = useState(true);
  const [popupOpen, setPopupOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const initAuth0 = async () => {
      // initOptions.language = "sv"
      initOptions.screen_hint = AUTH_0_LOGIN;
      const auth0FromHook = await createAuth0Client(initOptions);
      setAuth0(auth0FromHook);

      if (window?.location?.search?.includes('code=')) {
        const { appState } = await auth0FromHook?.handleRedirectCallback();
        const isAuthenticatedFromHook = await auth0FromHook?.isAuthenticated();

        if (isAuthenticatedFromHook) {
          navigate(appState?.targetUrl);
        }
      }

      const isAuthenticatedFromHook = await auth0FromHook.isAuthenticated();
      setIsAuthenticated(isAuthenticatedFromHook);

      if (isAuthenticatedFromHook) {
        const accessToken = await auth0FromHook.getTokenSilently();
        const user = await auth0FromHook.getUser();
        setUser(user);
        const userReducer = {
          accessToken: accessToken,
          isAuth0Authenticated: isAuthenticatedFromHook,
          userInfo: user,
        };
        dispatch(setUserReducer(userReducer));
        setCookie('userReducer', { accessToken }, 365);
      } else {
        dispatch(resetUserReducer());
        setCookie('userReducer', null, 0);
      }

      setLoading(false);
    };
    initAuth0();
    // eslint-disable-next-line
  }, []);

  const loginWithPopup = async (params = {}) => {
    setPopupOpen(true);
    try {
      // params.language = "sv"
      await auth0Client.loginWithPopup(params);
    } catch (error) {
      console.error(error);
    } finally {
      setPopupOpen(false);
    }
    const user = await auth0Client.getUser();
    setUser(user);
    setIsAuthenticated(true);
  };

  const handleRedirectCallback = async () => {
    setLoading(true);
    await auth0Client.handleRedirectCallback();
    const user = await auth0Client.getUser();
    setLoading(false);
    setIsAuthenticated(true);
    setUser(user);
  };

  return (
    <Auth0Context.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        popupOpen,
        loginWithPopup,
        handleRedirectCallback,
        getIdTokenClaims: auth0Client
          ? (...p) => auth0Client.getIdTokenClaims(...p)
          : undefined,
        loginWithRedirect: auth0Client
          ? (...p) => auth0Client.loginWithRedirect(...p)
          : undefined,
        getTokenSilently: auth0Client
          ? (...p) => auth0Client.getTokenSilently(...p)
          : undefined,
        getTokenWithPopup: auth0Client
          ? (...p) => auth0Client.getTokenWithPopup(...p)
          : undefined,
        logout: auth0Client ? (...p) => auth0Client.logout(...p) : undefined,
      }}
    >
      {children}
    </Auth0Context.Provider>
  );
};
